import {
  deleteFinalizedAuthorizationBundle,
  deleteOfficeManagerFlagsForCase,
} from '@/lib/surgeon-console/finalizedAuthorization';

export interface DemoPresentationState {
  signedPreAuthCaseId: string | null;
  postOpSignedCaseId: string | null;
  updatedAt: string | null;
}

type DemoPresentationListener = (state: DemoPresentationState) => void;

const DEMO_PRESENTATION_STATE_KEY = 'link.demoPresentationState.v1';
const DEMO_PRESENTATION_EVENT = 'link:demo-presentation-state';

const DEFAULT_DEMO_PRESENTATION_STATE: DemoPresentationState = {
  signedPreAuthCaseId: null,
  postOpSignedCaseId: null,
  updatedAt: null,
};

const listeners = new Set<DemoPresentationListener>();
let storageListenerAttached = false;

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readState(): DemoPresentationState {
  if (!canUseStorage()) return DEFAULT_DEMO_PRESENTATION_STATE;

  try {
    const raw = window.localStorage.getItem(DEMO_PRESENTATION_STATE_KEY);
    if (!raw) return DEFAULT_DEMO_PRESENTATION_STATE;
    const parsed = JSON.parse(raw) as Partial<DemoPresentationState>;
    return {
      signedPreAuthCaseId: canonicalDemoCaseId(parsed.signedPreAuthCaseId),
      postOpSignedCaseId: canonicalDemoCaseId(parsed.postOpSignedCaseId),
      updatedAt: parsed.updatedAt || null,
    };
  } catch {
    return DEFAULT_DEMO_PRESENTATION_STATE;
  }
}

function writeState(state: DemoPresentationState): void {
  if (!canUseStorage()) return;

  try {
    window.localStorage.setItem(DEMO_PRESENTATION_STATE_KEY, JSON.stringify(state));
  } catch {
    // Demo presentation state should never interrupt clinical workflow rendering.
  }
}

function notifyDemoPresentationStateChanged(state = readState()): void {
  listeners.forEach(listener => listener(state));

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(DEMO_PRESENTATION_EVENT, { detail: state }));
  }
}

function ensureStorageListener(): void {
  if (storageListenerAttached || typeof window === 'undefined') return;
  storageListenerAttached = true;

  window.addEventListener('storage', (event) => {
    if (event.key === DEMO_PRESENTATION_STATE_KEY) {
      notifyDemoPresentationStateChanged(readState());
    }
  });
}

function updateState(updater: (state: DemoPresentationState) => DemoPresentationState): DemoPresentationState {
  const nextState = updater(readState());
  writeState(nextState);
  notifyDemoPresentationStateChanged(nextState);
  return nextState;
}

export function canonicalDemoCaseId(id: string | null | undefined): string | null {
  const trimmed = String(id || '').trim();
  if (!trimmed) return null;

  return trimmed.startsWith('demo-post-op-')
    ? trimmed.replace(/^demo-post-op-/, '')
    : trimmed;
}

export function getDemoPresentationState(): DemoPresentationState {
  return readState();
}

export function markDemoPreAuthSigned(caseId: string | null | undefined): DemoPresentationState {
  const canonicalCaseId = canonicalDemoCaseId(caseId);
  if (!canonicalCaseId) return readState();

  return updateState(() => ({
    signedPreAuthCaseId: canonicalCaseId,
    postOpSignedCaseId: null,
    updatedAt: new Date().toISOString(),
  }));
}

export function markDemoPostOpSigned(caseId: string | null | undefined): DemoPresentationState {
  const canonicalCaseId = canonicalDemoCaseId(caseId);
  if (!canonicalCaseId) return readState();

  return updateState((state) => ({
    signedPreAuthCaseId: state.signedPreAuthCaseId || canonicalCaseId,
    postOpSignedCaseId: canonicalCaseId,
    updatedAt: new Date().toISOString(),
  }));
}

export function resetDemoPatientState(caseId: string | null | undefined): DemoPresentationState {
  const canonicalCaseId = canonicalDemoCaseId(caseId);
  if (!canonicalCaseId) return readState();

  deleteFinalizedAuthorizationBundle(canonicalCaseId);
  deleteOfficeManagerFlagsForCase(canonicalCaseId);

  return updateState((state) => ({
    signedPreAuthCaseId: state.signedPreAuthCaseId === canonicalCaseId ? null : state.signedPreAuthCaseId,
    postOpSignedCaseId: state.postOpSignedCaseId === canonicalCaseId ? null : state.postOpSignedCaseId,
    updatedAt: new Date().toISOString(),
  }));
}

export function subscribeDemoPresentationState(listener: DemoPresentationListener): () => void {
  ensureStorageListener();
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}
